import { Paper, Typography } from "@material-ui/core";
import TasksPlaceholder from "../tasks/TasksPlaceholder";
import { AgreementUserRole, QuestCompletionStatus, TaskStatus, UserQuestsConnectionWhere } from "../../../../models/generated/graphql";
import { useQuery } from "@apollo/client";
import LoadingBox from "../../../../components/LoadingBox";
import { useTranslation } from "react-i18next";
import { GET_INBOX_QUESTS } from "./queries";
import { InboxQuestProps, mapInboxQuests } from "./map-inbox-quests";
import { byDesc, sortByComparisons } from "@lyfegen-platform/utils";
import { GetInboxQuestsQuery } from "./types/queries.generated";
import VariableSizeListComponent from "../../../../components/VariableSizeListComponent";
import InboxQuest from "./InboxQuest";
import { useEffect, useState } from "react";
import { QuestsType } from "../../../../models/notifications";

const numberOfQuestsToLoad = 30;

interface InboxQuestsProps {
	questFilter: UserQuestsConnectionWhere;
}

const Root: React.FC<InboxQuestsProps> = ({ questFilter }) => {
	const { t } = useTranslation();
	const [quests, setQuests] = useState<Array<InboxQuestProps>>([]);
	const [originalQuests, setOriginalQuests] = useState<Array<InboxQuestProps>>([]);

	const { data, loading, error } = useQuery<GetInboxQuestsQuery>(GET_INBOX_QUESTS, {
		fetchPolicy: "no-cache",
	});

	useEffect(() => {
		if (data) {
			const mappedQuests = mapInboxQuests(data, t);
			setOriginalQuests(mappedQuests);
			setQuests(filterAndSortQuests(mappedQuests));
		}
	}, [data]);

	useEffect(() => {
		setQuests(filterQuests(originalQuests));
	}, [questFilter]);

	const handleQuestUpdate = (index: number, updatedQuest: any) => {
		const updatedOriginalQuests = originalQuests.map((quest, idx) => (idx === index ? { ...quest, ...updatedQuest } : quest));
		setOriginalQuests(updatedOriginalQuests);
		setQuests(filterQuests(updatedOriginalQuests));
	};

	const filterQuests = (quests: Array<InboxQuestProps>) => {
		const filter = questFilter.node;
		if (!filter) {
			return quests;
		}

		return quests
			.filter(
				(quest) =>
					(quest.triggerEventId === QuestsType.UnmatchedRecords && filter.triggerEventId_IN!.includes(quest.triggerEventId)) ||
					(![AgreementUserRole.LimitedAccessUser, AgreementUserRole.FullAccessUser].includes(quest.userRole) &&
						filter.triggerEventId_IN!.includes(quest.triggerEventId))
			)
			.filter((quest) => !filter.completionStatus || quest.questCompletionStatus === filter.completionStatus)
			.filter(
				(quest) =>
					!filter.timeStamp_GTE ||
					!filter.timeStamp_LTE ||
					(quest.questDate >= filter.timeStamp_GTE && quest.questDate <= filter.timeStamp_LTE)
			)
			.filter((quest) => !filter.case?.caseAssignee_SOME || quest.isAssignee)
			.filter(
				(quest) =>
					!filter.usersRelationshipConnection_SOME?.node?.id ||
					quest.questOwner?.id === filter.usersRelationshipConnection_SOME.node.id
			)
			.filter((quest) => !filter.questContentEn_CONTAINS || quest.questContentEn.includes(filter.questContentEn_CONTAINS))
			.filter((quest) => !filter.questContentDe_CONTAINS || quest.questContentDe.includes(filter.questContentDe_CONTAINS));
	};

	const sortQuests = (quests: Array<InboxQuestProps>) => {
		return (quests || []).sort(
			sortByComparisons(
				byDesc(
					(e) =>
						e &&
						e.isAssignee &&
						e.questCompletionStatus === QuestCompletionStatus.Active &&
						(!e.questStatus || (e.questStatus && e.questStatus === TaskStatus.Unread))
				),
				byDesc(
					(e) =>
						e &&
						!e.isAssignee &&
						e.questCompletionStatus === QuestCompletionStatus.Active &&
						(!e.questStatus || (e.questStatus && e.questStatus === TaskStatus.Unread))
				),
				byDesc(
					(e) =>
						e &&
						e.questCompletionStatus === QuestCompletionStatus.Active &&
						e.questStatus &&
						e.questStatus === TaskStatus.Unread
				),
				byDesc(
					(e) =>
						e && e.questCompletionStatus === QuestCompletionStatus.Active && e.questStatus && e.questStatus === TaskStatus.Read
				),
				byDesc(
					(e) =>
						e &&
						e.questCompletionStatus !== QuestCompletionStatus.Active &&
						e.questStatus &&
						e.questStatus === TaskStatus.Unread
				),
				byDesc(
					(e) =>
						e && e.questCompletionStatus !== QuestCompletionStatus.Active && e.questStatus && e.questStatus === TaskStatus.Read
				),
				byDesc((e) => e && e.questDate)
			)
		);
	};

	const filterAndSortQuests = (quests: Array<InboxQuestProps>) => {
		return sortQuests(filterQuests(quests));
	};

	if (loading) {
		return <LoadingBox />;
	}

	if (error) {
		return (
			<Typography color="error">
				{t("error")}
				{console.error(error)}
			</Typography>
		);
	}

	return (
		<div>
			{!loading && !!!quests?.length && <TasksPlaceholder />}
			{!loading && !!quests?.length && (
				<Paper style={{ boxShadow: "none" }}>
					<VariableSizeListComponent
						key={quests.length}
						data={quests}
						height={480}
						RowComponent={InboxQuest}
						onUpdate={handleQuestUpdate}
						initialValue={numberOfQuestsToLoad}
						loadMoreValue={numberOfQuestsToLoad}
					/>
				</Paper>
			)}
		</div>
	);
};

export default Root;
